import React from 'react';
import { Button, Progress } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCog, faWarehouse, faCircleCheck, faClipboardCheck, faFileImport,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource, createGridOptions, DataGrid, indexCellRenderer, LinkCellRenderer, TextFilterDefaults, IconCellRenderer,
    LinkIconRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext, { agGridConstants, ApiRoutes, AppNavPaths } from '../Common';
import { PageHeading, PageWrap, SmallButton } from '../common/forms/FormElements';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import authService from '../api-authorization/AuthorizeService';
import { ManageTenant } from '../tenant/ManageTenant';
import { ManageTenantTheme } from '../tenant/ManageTenantTheme';
import { ManageTenantHolidays } from '../tenant/ManageTenantHolidays';
import { showOnboardingChecklist } from '../tenant/OnboardingChecklistModal';

const NO_ADMIN_TITLE = "You don't have admin access for this tenant";

// Each disabled renderer wraps the same control as its active counterpart so
// row height and column alignment stay identical — just disabled and muted.
const DisabledNameRenderer = (params) => (
    <Button
        color="link"
        className="site-link text-muted"
        disabled
        title={NO_ADMIN_TITLE}
    >
        {params.data?.name}
    </Button>
);

const DisabledIconRenderer = (params) => (
    <SmallButton
        type="button"
        disabled
        title={NO_ADMIN_TITLE}
    >
        <i className={`fa ${params.iconClass} fa-md mr-2 text-muted`} />
    </SmallButton>
);

const DisabledLinkIconRenderer = (params) => (
    <SmallButton
        type="button"
        disabled
        className="icon-link"
        title={NO_ADMIN_TITLE}
    >
        <FontAwesomeIcon
            icon={params.iconClass}
            className="text-muted"
        />
    </SmallButton>
);

// Checklist column icon: shows a green check once every onboarding step has data
// (server-detected), otherwise the clipboard icon. Reuses the same query key as the
// onboarding modal, so the status is fetched once and shared/cached.
const ChecklistCellRenderer = (params) => {
    const tenantId = params.data?.id;

    const { data: status } = useQuery({
        queryKey: ['tenantOnboardingStatus', tenantId],
        queryFn: () => httpClient.js(ApiRoutes.tenant.onboardingStatus(tenantId)),
        enabled: !!tenantId,
    });

    const isComplete = Array.isArray(status) && status.length > 0 && status.every((s) => s.isComplete);

    return (
        <SmallButton
            type="button"
            onClick={() => params.clicked?.(tenantId, params.data)}
            title={isComplete ? 'Onboarding complete' : 'Onboarding Checklist'}
        >
            <FontAwesomeIcon
                icon={isComplete ? faCircleCheck : faClipboardCheck}
                className={isComplete ? 'text-success' : 'text-danger'}
            />
        </SmallButton>
    );
};

class TenantIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.companyFormRef = React.createRef();
        this.tenantThemeFormRef = React.createRef();
        this.tenantHolidaysFormRef = React.createRef();
        this.state = {
            loading: true,
        };
    }

    componentDidMount() {
        return this.populateState();
    }

    onViewTenant = (id) => {
        this.companyFormRef.current.open(id); this.setState({ showTenantForm: true });
    };

    onViewTheme = (id) => {
        this.tenantThemeFormRef.current.open(id); this.setState({ showTenantThemeForm: true });
    };

    onViewHolidays = (id) => {
        this.tenantHolidaysFormRef.current.open(id); this.setState({ showTenantHolidaysForm: true });
    };

    onTenantFormClosed = () => {
        this.setState({ showTenantForm: false });
        this.setState({ showTenantThemeForm: false });
        this.setState({ showTenantHolidaysForm: false });
        if (this.state.gridOptions) {
            this.state.gridOptions.refresh();
        }
    };

    isAdminFor = (params) => {
        const { adminTenantIds } = this.state;
        const id = params?.data?.id;
        return Array.isArray(adminTenantIds) && id != null && adminTenantIds.includes(id);
    };

    populateState = async () => {
        const [isAuthenticated] = await Promise.all([authService.isAuthenticated(), authService.getUser()]);

        if (isAuthenticated) {
            const [usStates, adminTenantIds] = await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.USStates()),
                httpClient.js(ApiRoutes.tenant.myAdminTenantIds()).catch(() => []),
            ]);

            const gridOptions = createGridOptions(this);

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                iconRenderer: IconCellRenderer,
                nameRenderer: LinkCellRenderer,
                linkIconRenderer: LinkIconRenderer,
                disabledNameRenderer: DisabledNameRenderer,
                disabledIconRenderer: DisabledIconRenderer,
                disabledLinkIconRenderer: DisabledLinkIconRenderer,
                checklistStatusRenderer: ChecklistCellRenderer,
            };

            gridOptions.columnDefs = [
                {
                    flex: 0,
                    width: 80,
                    headerName: '',
                    valueGetter: 'node.id',
                    sortable: false,
                    cellRenderer: indexCellRenderer,
                },
                {
                    colId: 'CompanyName',
                    sortable: true,
                    headerName: 'Name',
                    field: 'name',
                    filter: agGridConstants.columnFilterTypes.text,
                    floatingFilter: true,
                    filterParams: TextFilterDefaults,
                    sort: { direction: 'asc', priority: 0 },
                    cellRendererSelector: (params) => (this.isAdminFor(params)
                        ? {
                            component: 'nameRenderer',
                            params: {
                                clicked: this.onViewTenant,
                                nameField: 'name',
                                idField: 'id',
                                title: 'View this Tenant',
                            },
                        }
                        : { component: 'disabledNameRenderer' }),
                },
                {
                    colId: 'Id',
                    flex: 0,
                    width: 20,
                    headerName: 'Theme',
                    sortable: false,
                    cellRendererSelector: (params) => (this.isAdminFor(params)
                        ? {
                            component: 'iconRenderer',
                            params: {
                                clicked: this.onViewTheme,
                                idField: 'id',
                                iconClass: 'fa-palette',
                                nameField: 'Ho',
                            },
                        }
                        : { component: 'disabledIconRenderer', params: { iconClass: 'fa-palette' } }),
                },
                {
                    colId: 'Holidays',
                    flex: 0,
                    width: 20,
                    headerName: 'Holidays',
                    sortable: false,
                    cellRendererSelector: (params) => (this.isAdminFor(params)
                        ? {
                            component: 'iconRenderer',
                            params: {
                                clicked: this.onViewHolidays,
                                idField: 'id',
                                nameField: 'Manage Holiday Pay Rules',
                                iconClass: 'fa-calendar-check',
                            },
                        }
                        : { component: 'disabledLinkIconRenderer', params: { iconClass: faCog } }),
                },
                {
                    flex: 0,
                    colId: 'Settings',
                    sortable: false,
                    headerName: 'Settings',
                    cellRendererSelector: (params) => (this.isAdminFor(params)
                        ? {
                            component: 'linkIconRenderer',
                            params: {
                                to: (id) => `${AppNavPaths.Tenant}/${id}`,
                                idField: 'id',
                                title: 'View this Tenant Settings',
                                iconClass: faCog,
                            },
                        }
                        : { component: 'disabledLinkIconRenderer', params: { iconClass: faCog } }),
                },
                {
                    colId: 'Checklist',
                    flex: 0,
                    width: 20,
                    headerName: 'Checklist',
                    sortable: false,
                    cellRendererSelector: (params) => (this.isAdminFor(params)
                        ? {
                            component: 'checklistStatusRenderer',
                            params: {
                                clicked: (id) => showOnboardingChecklist(id),
                            },
                        }
                        : { component: 'disabledIconRenderer', params: { iconClass: 'fa-clipboard-check' } }),
                },
                {
                    colId: 'Location.Address1',
                    sortable: false,
                    headerName: 'Address Line 1',
                    field: 'address1',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
                {
                    colId: 'Location.Address2',
                    sortable: false,
                    headerName: 'Address Line 2',
                    field: 'address2',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
                {
                    colId: 'Location.City',
                    sortable: true,
                    headerName: 'City',
                    field: 'city',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
                {
                    flex: 0,
                    width: 140,
                    colId: 'Location.State',
                    sortable: true,
                    headerName: 'State',
                    field: 'state',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: {
                        labelText: 'Filter by State',
                        options: usStates,
                        optionsLabel: 'label',
                        optionsValue: 'value',
                    },
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                        options: usStates,
                        optionsLabel: 'label',
                        optionsValue: 'value',
                    },
                },
                {
                    flex: 0,
                    width: 160,
                    colId: 'Location.Zip',
                    sortable: true,
                    headerName: 'Zip',
                    field: 'zip',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
                {
                    colId: 'Location.Phone',
                    sortable: true,
                    headerName: 'Phone',
                    field: 'phone',
                    filter: agGridConstants.columnFilterTypes.text,
                    filterParams: TextFilterDefaults,
                    floatingFilter: true,
                },
            ];



            const ds = createDataSource(ApiRoutes.tenant.search(), gridOptions);

            this.setState({
                isAuthenticated,
                loading: false,
                gridOptions,
                dataSource: ds,
                adminTenantIds: adminTenantIds ?? [],
            });
        } else {
            // redirect to access denied?
        }
    };

    render() {
        const { isAuthenticated, rowData, gridOptions } = this.state;
        if (this.state.loading || !isAuthenticated || !this.context.user) return (<Progress />);
        if (!isAuthenticated || !this.context.user.isAdmin) return ''; // probably redundant
        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faWarehouse}
                        className="mr-2 text-muted"
                    />
                    <span>Tenants</span>
                </PageHeading>
                <DataGridToolbar
                    entity="company"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    onAdd={() => {
                        this.setState({ showTenantForm: true });
                        if (this.companyFormRef.current) {
                            this.companyFormRef.current.open();
                        }
                    }}
                    addLabel="Add Tenant"
                >
                    <Link
                        component={SmallButton}
                        type="button"
                        to={`${AppNavPaths.SupportTools}/import-customer-data`}
                    >
                        <FontAwesomeIcon
                            icon={faFileImport}
                            className="mr-2"
                        />
                        <span className="small-viewport-hide">
                            Import Customer Data
                        </span>
                    </Link>
                </DataGridToolbar>
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}

                />

                <ManageTenant
                    show={this.state.showTenantForm}
                    toggleShow={(show) => this.setState({ showTenantForm: show })}
                    ref={this.companyFormRef}
                    onClose={this.onTenantFormClosed}
                    onCreated={showOnboardingChecklist}
                />
                <ManageTenantTheme
                    show={this.state.showTenantThemeForm}
                    toggleShow={(show) => this.setState({ showTenantThemeForm: show })}
                    ref={this.tenantThemeFormRef}
                    onClose={this.onTenantFormClosed}
                />
                <ManageTenantHolidays
                    show={this.state.showTenantHolidaysForm}
                    toggleShow={(show) => this.setState({ showTenantHolidaysForm: show })}
                    ref={this.tenantHolidaysFormRef}
                    onClose={this.onTenantFormClosed}
                />
            </PageWrap>
        );
    }
}
export default TenantIndex;
